import { SITE_URL, getAbsoluteSiteUrl } from '@/data/blogSeoMetadata';

export { SITE_URL, getAbsoluteSiteUrl };

export const PNP_PAGE_PATH = '/personalised-nutrition-platform';
export const PNP_PAGE_CANONICAL_URL = getAbsoluteSiteUrl(PNP_PAGE_PATH);

export const PNP_PAGE_TITLE = 'Personalised Nutrition Platform for UK Practitioners | CalorieScience';

export const PNP_META_DESCRIPTION =
  'CalorieScience is an AI-assisted personalised nutrition platform for UK practitioners, connect biomarkers, genetics, and lifestyle data to build evidence-based, personalised meal plans.';

export const PNP_PRIMARY_KEYWORD = 'Personalised Nutrition Platform';

export const PNP_SECONDARY_KEYWORDS = [
  'Personalised nutrition platform technology',
  'Personalised Nutrition Platform for Practitioners',
  'AI-powered personalised nutrition platforms',
  'Modern nutrition platforms',
];

export const PNP_REVIEWED_BY = 'Vandana Pramod';
export const PNP_LAST_REVIEWED_DISPLAY = '31 July 2026';
export const PNP_DEMO_VIDEO_URL = 'https://www.youtube.com/shorts/tmd1W2bKQ0Y';

export const PNP_WORKFLOW_IMAGE_DESKTOP = '/images/pnp/workflow-desktop.png';
export const PNP_WORKFLOW_IMAGE_MOBILE = '/images/pnp/workflow-mobile.png';

export type PnpFaq = {
  question: string;
  answer: string;
};

export const PNP_FAQS: PnpFaq[] = [
  {
    question: 'What is a personalised nutrition platform?',
    answer:
      'A personalised nutrition platform helps practitioners create nutrition recommendations based on individual client data such as biomarkers, dietary intake, health conditions and lifestyle factors, rather than generic, population-level advice.',
  },
  {
    question: 'Is personalised nutrition evidence-based?',
    answer:
      'Systematic reviews and controlled trials indicate that personalised nutrition interventions, especially when delivered digitally and grounded in nutrition science, can improve dietary behaviour more than generalised advice in many contexts. However, effectiveness depends on programme design, quality of data, behaviour-change support and how well recommendations are tailored to the individual.',
  },
  {
    question: 'Does personalised nutrition replace medical care?',
    answer:
      'No. UK organisations emphasise that personalised nutrition is complementary to conventional medical care and should not be used as a substitute for diagnosis or treatment. CalorieScience is a professional tool to support nutrition-related decision-making; practitioners must work within their scope of practice and collaborate with medical teams where appropriate.',
  },
  {
    question: 'How does personalised meal planning work?',
    answer:
      'It uses client-specific information, biomarkers, preferences, health conditions and goals, to generate a plan aligned with that individual’s needs, which the practitioner then reviews and adjusts before it reaches the client.',
  },
  {
    question: 'Can biomarkers improve nutrition recommendations?',
    answer:
      'Biomarkers provide additional context about an individual’s health status and can help practitioners make more informed nutrition decisions when interpreted appropriately, alongside symptoms, history and goals.',
  },
  {
    question: 'Can I use genetic and microbiome tests within CalorieScience?',
    answer:
      'Current evidence suggests nutrigenetic and microbiome-based insights can add nuance to dietary advice, but they should be interpreted cautiously and alongside diet, phenotype and lifestyle information. CalorieScience can help you record and organise such test results as part of the client’s profile, leaving you free to decide how much weight to give them in your recommendations.',
  },
  {
    question: 'Who can use a personalised nutrition platform?',
    answer:
      'Dietitians, nutritionists, clinical nutrition practitioners, nutritional therapists, health coaches and healthcare organisations can all use personalised nutrition platforms, within their respective scopes of practice.',
  },
  {
    question: 'Is this suitable for NHS or UK regulatory environments?',
    answer:
      'Many UK dietitians and nutrition professionals already use digital tools for dietary assessment and analysis, and UK-based software providers emphasise data security, transparency and alignment with professional standards. CalorieScience is designed to support responsible, evidence-informed practice, but each organisation remains responsible for ensuring that the way they use any digital tool complies with their local policies, data protection obligations and professional guidelines.',
  },
  {
    question: 'Does AI replace nutrition professionals?',
    answer:
      'No. AI can assist with analysis, organisation and workflow automation, but clinical judgement remains the responsibility of the practitioner.',
  },
  {
    question: 'Which personalised nutrition company is best?',
    answer:
      'It depends what you’re looking for. Consumer-facing services like ZOE offer personalised nutrition directly to individuals through a home-testing kit and app, without a practitioner involved. CalorieScience is a different category: a platform built for registered dietitians, nutritionists and nutritional therapists to deliver personalised, biomarker-informed nutrition care to their own clients, with the practitioner reviewing and approving every recommendation. If you’re a practitioner looking for software rather than a personal nutrition service, that distinction is the main thing to check before comparing providers.',
  },
];
